import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft, Star, Gift, Users, Copy, Check, TrendingUp, Clock, Zap, Award } from 'lucide-react';
import { getLoyaltyBalance, getLoyaltyHistory, redeemLoyaltyPoints, getReferralInfo } from '../api';
import { toast } from '../hooks/use-toast';

const ClientLoyalty = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('points');
  const [loyaltyData, setLoyaltyData] = useState(null);
  const [history, setHistory] = useState([]);
  const [referralData, setReferralData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [redeeming, setRedeeming] = useState(null);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/client/auth/me`, { credentials: 'include' });
        if (!response.ok) navigate('/portal/login');
      } catch { navigate('/portal/login'); }
    };
    checkAuth();
  }, [navigate]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [balance, hist, referral] = await Promise.all([
          getLoyaltyBalance(), getLoyaltyHistory(), getReferralInfo()
        ]);
        setLoyaltyData(balance);
        setHistory(hist.transactions || []);
        setReferralData(referral);
      } catch (err) { console.error(err); } finally { setLoading(false); }
    };
    loadData();
  }, []);

  const handleRedeem = async (rewardId) => {
    setRedeeming(rewardId);
    try {
      const result = await redeemLoyaltyPoints(rewardId);
      toast({ title: 'Reward Redeemed!', description: result.message });
      const [balance, hist] = await Promise.all([getLoyaltyBalance(), getLoyaltyHistory()]);
      setLoyaltyData(balance);
      setHistory(hist.transactions || []);
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to redeem', variant: 'destructive' });
    } finally { setRedeeming(null); }
  };

  const handleCopyCode = () => {
    if (referralData?.code) {
      navigator.clipboard.writeText(referralData.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast({ title: 'Copied!', description: 'Referral code copied to clipboard' });
    }
  };

  const tierMultiplierLabel = { 1: '1x', 1.5: '1.5x', 2: '2x' };

  if (loading) return <div className="min-h-screen bg-gray-100 flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600" /></div>;

  return (
    <div className="min-h-screen bg-gray-100" data-testid="client-loyalty-page">
      <header className="bg-black text-white py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/portal')} className="text-gray-300 hover:text-white">
              <ArrowLeft className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Back</span>
            </Button>
            <h1 className="text-lg sm:text-xl font-bold">Rewards & Referrals</h1>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Points Balance Card */}
        <Card className="p-6 mb-6 bg-black text-white border-0" data-testid="loyalty-balance-card">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <p className="text-gray-400 text-sm mb-1">Your Points Balance</p>
              <p className="text-4xl sm:text-5xl font-bold text-red-500" data-testid="points-balance">{loyaltyData?.balance?.toLocaleString() || 0}</p>
              <div className="flex items-center gap-3 mt-2">
                <span className="text-xs text-gray-400">Earned: {loyaltyData?.total_earned?.toLocaleString() || 0}</span>
                <span className="text-xs text-gray-400">Redeemed: {loyaltyData?.total_redeemed?.toLocaleString() || 0}</span>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
              <Zap className="h-4 w-4 text-yellow-400" />
              <span className="text-sm font-semibold">{tierMultiplierLabel[loyaltyData?.tier_multiplier] || '1x'} Points</span>
              <span className="text-xs text-gray-400 capitalize">({loyaltyData?.tier} tier)</span>
            </div>
          </div>
        </Card>

        {/* Tab Toggle */}
        <div className="flex gap-2 mb-6">
          {[
            { id: 'points', label: 'Rewards', icon: Gift },
            { id: 'referral', label: 'Referral', icon: Users },
            { id: 'history', label: 'History', icon: Clock },
          ].map(({ id, label, icon: Icon }) => (
            <Button key={id} variant={tab === id ? 'default' : 'outline'} size="sm" onClick={() => setTab(id)}
              className={tab === id ? 'bg-red-600 hover:bg-red-700' : 'border-gray-300'} data-testid={`tab-${id}`}>
              <Icon className="h-4 w-4 mr-2" />{label}
            </Button>
          ))}
        </div>

        {/* Rewards Tab */}
        {tab === 'points' && (
          <div className="grid sm:grid-cols-2 gap-4" data-testid="rewards-grid">
            {(loyaltyData?.rewards || []).map((reward) => (
              <Card key={reward.id} className={`p-5 border-gray-200 ${!reward.available ? 'opacity-60' : ''}`} data-testid={`reward-${reward.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="h-10 w-10 bg-red-100 rounded-lg flex items-center justify-center">
                    <Star className="h-5 w-5 text-red-600" />
                  </div>
                  <span className="text-sm font-bold text-red-600">{reward.points} pts</span>
                </div>
                <h4 className="font-bold text-black mb-1">{reward.name}</h4>
                <p className="text-sm text-gray-600 mb-4">{reward.description}</p>
                <Button onClick={() => handleRedeem(reward.id)} disabled={!reward.available || redeeming === reward.id}
                  size="sm" className={`w-full ${reward.available ? 'bg-red-600 hover:bg-red-700' : 'bg-gray-300'}`}
                  data-testid={`redeem-${reward.id}`}>
                  {redeeming === reward.id ? 'Redeeming...' : reward.available ? 'Redeem' : 'Not Enough Points'}
                </Button>
              </Card>
            ))}
          </div>
        )}

        {/* Referral Tab */}
        {tab === 'referral' && referralData && (
          <div className="space-y-6" data-testid="referral-section">
            <Card className="p-6 border-gray-200">
              <h3 className="text-lg font-bold text-black mb-2">Your Referral Code</h3>
              <p className="text-sm text-gray-600 mb-4">Share your code with friends. When they book, you both earn rewards!</p>
              <div className="flex items-center gap-3 mb-6">
                <div className="flex-1 bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg px-6 py-4 text-center">
                  <span className="text-2xl sm:text-3xl font-bold tracking-widest text-black" data-testid="referral-code">{referralData.code}</span>
                </div>
                <Button onClick={handleCopyCode} variant="outline" className="h-14 px-4" data-testid="copy-referral-btn">
                  {copied ? <Check className="h-5 w-5 text-green-600" /> : <Copy className="h-5 w-5" />}
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-xs text-green-600 font-semibold mb-1">YOU GET</p>
                  <p className="font-bold text-black text-sm">500 points + 1 free hour</p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-xs text-blue-600 font-semibold mb-1">FRIEND GETS</p>
                  <p className="font-bold text-black text-sm">250 bonus points</p>
                </div>
              </div>
            </Card>

            <Card className="p-6 border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-bold text-black">Referral Stats</h3>
                <div className="flex items-center gap-2">
                  <Award className="h-5 w-5 text-red-600" />
                  <span className="font-bold text-red-600">{referralData.total_points_earned} pts earned</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-2xl font-bold text-black">{referralData.total_referrals}</p>
                  <p className="text-xs text-gray-500">Successful Referrals</p>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-2xl font-bold text-black">{referralData.referrals?.length || 0}</p>
                  <p className="text-xs text-gray-500">Total Invites</p>
                </div>
              </div>
              {referralData.referrals?.length > 0 && (
                <div className="space-y-2">
                  {referralData.referrals.map((ref, idx) => (
                    <div key={idx} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                      <div className="flex items-center gap-2">
                        <div className={`h-2 w-2 rounded-full ${ref.status === 'completed' ? 'bg-green-500' : 'bg-yellow-500'}`} />
                        <span className="text-sm text-gray-700">{ref.referee_email}</span>
                      </div>
                      <span className={`text-xs font-medium capitalize ${ref.status === 'completed' ? 'text-green-600' : 'text-yellow-600'}`}>{ref.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        )}

        {/* History Tab */}
        {tab === 'history' && (
          <Card className="border-gray-200" data-testid="loyalty-history">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-base font-bold text-black">Points History</h3>
            </div>
            {history.length === 0 ? (
              <div className="p-12 text-center text-gray-500">
                <Star className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p>No points history yet. Complete a booking to start earning!</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {history.map((tx, idx) => (
                  <div key={idx} className="flex items-center justify-between p-4 hover:bg-gray-50">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ${tx.type === 'earn' ? 'bg-green-100' : 'bg-red-100'}`}>
                        {tx.type === 'earn' ? <TrendingUp className="h-4 w-4 text-green-600" /> : <Gift className="h-4 w-4 text-red-600" />}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-black truncate">{tx.description}</p>
                        <p className="text-xs text-gray-500">{new Date(tx.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                      </div>
                    </div>
                    <span className={`text-sm font-bold flex-shrink-0 ${tx.type === 'earn' ? 'text-green-600' : 'text-red-600'}`}>
                      {tx.type === 'earn' ? '+' : '-'}{tx.points}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}

        {/* How It Works */}
        <Card className="p-6 mt-6 border-gray-200 bg-gray-50" data-testid="how-it-works">
          <h3 className="text-base font-bold text-black mb-4">How Points Work</h3>
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="h-10 w-10 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-2">
                <span className="text-white font-bold text-sm">1</span>
              </div>
              <p className="text-sm font-semibold text-black">Book & Ride</p>
              <p className="text-xs text-gray-600">Earn 10 pts per $1 spent</p>
            </div>
            <div className="text-center">
              <div className="h-10 w-10 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-2">
                <span className="text-white font-bold text-sm">2</span>
              </div>
              <p className="text-sm font-semibold text-black">Earn Bonuses</p>
              <p className="text-xs text-gray-600">Higher tiers = bigger multiplier</p>
            </div>
            <div className="text-center">
              <div className="h-10 w-10 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-2">
                <span className="text-white font-bold text-sm">3</span>
              </div>
              <p className="text-sm font-semibold text-black">Redeem Rewards</p>
              <p className="text-xs text-gray-600">Free hours, upgrades & more</p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ClientLoyalty;
